import {
  GetDigitalAndPaperDraftsByEventIdQuery,
  StationeryDraftFormat,
  StationeryTemplateCategoryEnum,
  useGetDigitalAndPaperDraftsByEventIdQuery,
  useGetStationeryPrintOrdersQuery
} from '@graphql/generated';
import { useCallback, useMemo } from 'react';
import { ReEngagementPopupItemTypes } from '../constants/ReEngagementPopup.const';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useHistory, useTranslation } from '@shared/core';
import { useDashboardTelemetry } from '@apps/admin/routes/Dashboard/Dashboard.telemetry';

const usePrintNotification = (args: { isClosed: boolean }) => {
  const { isClosed } = args;
  const { eventInfo, eventHandle } = useEventInfo();
  const history = useHistory();
  const { printReEngagementPopupCTAClicked } = useDashboardTelemetry();
  const { t } = useTranslation('adminDashboardV2');
  const popuText = t('reEngagementPopup');

  const { data: stationeryDraftsData } = useGetDigitalAndPaperDraftsByEventIdQuery({
    variables: {
      input: {
        eventId: eventInfo?.eventId || ''
      },
      eventId: eventInfo?.eventId || ''
    },
    skip: !eventInfo?.eventId || isClosed,
    batchMode: 'slow'
  });

  const { data: printOrderData } = useGetStationeryPrintOrdersQuery({
    variables: {
      filters: {
        eventId: eventInfo?.eventId || ''
      }
    },
    skip: !eventInfo?.eventId || isClosed,
    batchMode: 'slow'
  });

  const config:
    | { type: StationeryTemplateCategoryEnum; draft: GetDigitalAndPaperDraftsByEventIdQuery['stationeryDrafts'][number] }
    | { type: 'reminder'; draftsWithoutOrder: number; lastDraft: GetDigitalAndPaperDraftsByEventIdQuery['stationeryDrafts'][number] }
    | null = useMemo(() => {
    if (!printOrderData || !stationeryDraftsData) return null;

    const drafts = stationeryDraftsData?.stationeryDrafts?.filter(draft => draft.format === StationeryDraftFormat.print);

    // draftId: orderId, category
    const map: Record<string, { orderId: string; category: StationeryTemplateCategoryEnum }> = printOrderData.stationeryPrintOrders.orders.reduce((acc, order) => {
      if (!order.cardDraft) return acc;

      return {
        ...acc,
        [order.cardDraft.id]: {
          orderId: order.id,
          category: order.cardDraft.stationeryTemplate.category
        }
      };
    }, {});

    const draftsWithoutOrder = drafts.filter(draft => !(draft.id in map)) || [];
    const sortedDraftsWithoutOrder = [...draftsWithoutOrder].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

    const lastSaveTheDateDraft = sortedDraftsWithoutOrder.find(draft => draft.stationeryTemplate.category === StationeryTemplateCategoryEnum.saveTheDate);
    const lastInvitationDraft = sortedDraftsWithoutOrder.find(draft => draft.stationeryTemplate.category === StationeryTemplateCategoryEnum.invitation);
    const lastThankYouDraft = sortedDraftsWithoutOrder.find(draft => draft.stationeryTemplate.category === StationeryTemplateCategoryEnum.thankYou);

    let hasSaveTheDateOrder = false;
    let hasInvitationOrder = false;
    let hasThankYouOrder = false;

    Object.values(map).forEach(order => {
      if (order.category === StationeryTemplateCategoryEnum.saveTheDate) {
        hasSaveTheDateOrder = true;
      }

      if (order.category === StationeryTemplateCategoryEnum.invitation) {
        hasInvitationOrder = true;
      }

      if (order.category === StationeryTemplateCategoryEnum.thankYou) {
        hasThankYouOrder = true;
      }
    });

    if (!hasThankYouOrder && lastThankYouDraft) {
      return {
        type: StationeryTemplateCategoryEnum.thankYou,
        draft: lastThankYouDraft
      };
    }

    if (!hasInvitationOrder && lastInvitationDraft) {
      return {
        type: StationeryTemplateCategoryEnum.invitation,
        draft: lastInvitationDraft
      };
    }

    if (!hasSaveTheDateOrder && lastSaveTheDateDraft) {
      return {
        type: StationeryTemplateCategoryEnum.saveTheDate,
        draft: lastSaveTheDateDraft
      };
    }

    if (draftsWithoutOrder.length && !printOrderData.stationeryPrintOrders.orders.length) {
      return {
        type: 'reminder',
        draftsWithoutOrder: draftsWithoutOrder.length,
        lastDraft: sortedDraftsWithoutOrder[0]
      };
    }

    return null;
  }, [stationeryDraftsData, printOrderData]);

  const handleOpenDrafts = useCallback(() => {
    printReEngagementPopupCTAClicked('viewDrafts', 'clicked');

    history.push(`/${eventHandle}/edit/cards/drafts`);
  }, [printReEngagementPopupCTAClicked, history, eventHandle]);

  const handleOpenDraft = useCallback(
    (id: string) => {
      printReEngagementPopupCTAClicked('editDraft', 'clicked');

      history.push(`/${eventHandle}/edit/cards/customize/${id}`);
    },
    [printReEngagementPopupCTAClicked, history, eventHandle]
  );

  return useMemo(() => {
    if (!config) return null;

    if (config.type === 'reminder') {
      return {
        key: ReEngagementPopupItemTypes.PRINT,
        canShow: !isClosed && config,
        title: popuText.print.title(),
        imageUrl: config.lastDraft.stationeryTemplate.thumbnailUrl || '',
        subtitle:
          config.draftsWithoutOrder === 1
            ? popuText.print.subTitle.singular({ numberOfDrafts: config.draftsWithoutOrder })
            : popuText.print.subTitle.plural({ numberOfDrafts: config.draftsWithoutOrder }),
        onClick: handleOpenDrafts
      };
    } else {
      let title = '';

      if (config.type === StationeryTemplateCategoryEnum.saveTheDate) {
        title = popuText.print.saveTheDateTitle();
      }

      if (config.type === StationeryTemplateCategoryEnum.invitation) {
        title = popuText.print.invitationTitle();
      }

      if (config.type === StationeryTemplateCategoryEnum.thankYou) {
        title = popuText.print.thankYouTitle();
      }

      return {
        key: ReEngagementPopupItemTypes.PRINT,
        canShow: !isClosed && config,
        title,
        imageUrl: config.draft.stationeryTemplate.thumbnailUrl || '',
        subtitle: popuText.print.cardSubtile(),
        onClick: () => handleOpenDraft(config.draft.id)
      };
    }
  }, [config, isClosed, popuText.print, handleOpenDrafts, handleOpenDraft]);
};

export default usePrintNotification;
